import { Eye } from "@tamagui/lucide-icons";
import { ServiceRequest, ServiceRequestStatus } from "common";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { YStack, XStack, Text } from "tamagui";
import { OutlinedButton, Table } from "tamagui-config";

import { StatusPill } from "../../components/StatusPill";
import { serviceTypeLabel } from "../../utils";
import { iconLabel } from "../../utils/a11y";

export interface ServiceRequestDisplay extends ServiceRequest {
  propertyId?: string;
  systemAlert?: string;
  customerRequest?: string;
  requestClosed?: string;
  requestedDate: string;
  requestedTimeSlot?: string | null;
  deviceRoom?: string;
  customerName: string;
  customerAddress?: string;
  notes?: string;
  hubName?: string;
}

export const ServiceRequestTableRow = ({
  request,
  onViewServiceRequest,
  isFirst,
  isLast,
  asCard,
}: {
  request: ServiceRequestDisplay;
  onViewServiceRequest: (id: string) => void;
  isFirst?: boolean;
  isLast?: boolean;
  asCard?: boolean;
}) => {
  const { t } = useTranslation();
  const getStatusBadge = (status: ServiceRequestStatus | string) => {
    switch (status) {
      case ServiceRequestStatus.OPEN:
        return (
          <StatusPill success>{t("serviceRequests.statuses.open")}</StatusPill>
        );
      case ServiceRequestStatus.PENDING:
        return (
          <StatusPill warning>
            {t("serviceRequests.statuses.pending")}
          </StatusPill>
        );
      case "in_progress":
        return (
          <StatusPill backgroundColor="$hgPrimary" borderColor="$hgPrimary">
            <Text color="$color1" fontSize="$3">
              {t("serviceRequests.statuses.inProgress")}
            </Text>
          </StatusPill>
        );
      case ServiceRequestStatus.CANCELED:
        return (
          <StatusPill error>
            {t("serviceRequests.statuses.canceled")}
          </StatusPill>
        );
      case ServiceRequestStatus.RESCHEDULE:
        return (
          <StatusPill info>
            {t("serviceRequests.statuses.reschedule")}
          </StatusPill>
        );
      case ServiceRequestStatus.CLOSED:
        return <StatusPill>{t("serviceRequests.statuses.closed")}</StatusPill>;
      default:
        return <StatusPill>{status}</StatusPill>;
    }
  };

  const customer = (
    <YStack flexShrink={1}>
      <Text fontWeight="bold" fontSize="$3">
        {request.customerName}
      </Text>
      <Text color="$gray10" fontSize="$2">
        {request.customerAddress}
      </Text>
    </YStack>
  );
  const serviceType = (
    <Text fontSize="$3">{serviceTypeLabel(t, request.serviceType)}</Text>
  );
  const systemAlert = <Text fontSize="$3">{request.systemAlert || "-"}</Text>;
  const customerRequest = (
    <YStack>
      <Text fontSize="$3">{request.customerRequest || "-"}</Text>
      {/* Requests since 2026-08 record who pressed the button; older rows
          (and the retired auto-created ones) have no requestedByType. */}
      {request.requestedByType === "USER" && (
        <Text color="$gray10" fontSize="$2">
          {t("serviceRequests.requestedVia.user", "via customer app")}
        </Text>
      )}
      {request.requestedByType === "HUB" && (
        <Text color="$gray10" fontSize="$2">
          {t("serviceRequests.requestedVia.hub", "via base station")}
        </Text>
      )}
    </YStack>
  );
  const requestClosed = (
    <Text fontSize="$3">{request.requestClosed || "-"}</Text>
  );
  const deviceRoom = <Text fontSize="$3">{request.deviceRoom}</Text>;
  const viewButton = (
    <OutlinedButton
      rounded
      icon={Eye}
      fontSize="$3"
      {...iconLabel(t("alerts.table.viewButton"))}
      onPress={() => onViewServiceRequest(request.id)}
    />
  );

  // Phone layout: same cells as the table row, one labelled line each —
  // styled like components/TableCards.tsx so every list reads the same.
  if (asCard) {
    const lines: [string, string, ReactNode][] = [
      ["status", "Status", getStatusBadge(request.status)],
      ["serviceType", "Service Type", serviceType],
      ["systemAlert", "System Alert", systemAlert],
      ["customerRequest", "Customer Request", customerRequest],
      ["requestClosed", "Request Closed", requestClosed],
      ["deviceRoom", "Device / Room", deviceRoom],
    ];
    return (
      <YStack
        tag="article"
        gap="$2.5"
        padding="$3.5"
        borderRadius="$6"
        borderWidth={1}
        borderColor="$borderColor"
        backgroundColor="$gray2"
      >
        <XStack gap="$3" alignItems="center" justifyContent="space-between">
          {customer}
          {viewButton}
        </XStack>
        {lines.map(([key, fallback, value]) => (
          <XStack
            key={key}
            gap="$3"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Text fontSize="$3" color="$gray11">
              {t(`serviceRequests.tableHeaders.${key}`, fallback)}
            </Text>
            <YStack flexShrink={1} alignItems="flex-end">
              {value}
            </YStack>
          </XStack>
        ))}
      </YStack>
    );
  }

  return (
    <Table.Row rowLocation={isFirst ? "first" : isLast ? "last" : "middle"}>
      <Table.Cell>{customer}</Table.Cell>
      <Table.Cell>{serviceType}</Table.Cell>
      <Table.Cell>{getStatusBadge(request.status)}</Table.Cell>
      <Table.Cell>{systemAlert}</Table.Cell>
      <Table.Cell>{customerRequest}</Table.Cell>
      <Table.Cell>{requestClosed}</Table.Cell>
      <Table.Cell>{deviceRoom}</Table.Cell>
      <Table.Cell>
        <XStack justifyContent="center">{viewButton}</XStack>
      </Table.Cell>
    </Table.Row>
  );
};
